import client from './client';
import {
  BackfillActivityListResponse,
  BackfillIngestRequest,
  BackfillIngestResult,
  BackfillPreviewRequest,
  BackfillPreviewResult,
  ChannelCompletionSummaryResponse,
  CompanyContentDetail,
  ContentCompletionOverviewResponse,
  ContentCompletionSortField,
  ContentHealthStatus,
  CorporateStatusFilter,
  CoveragePriorityFilter,
  FeedUrlIssueListResponse,
  FeedUrlIssueResponse,
  FeedUrlIssueStatus,
  QuartrCheckRequest,
  QuartrCheckResult,
  QuartrHealthStatus,
  QuartrLookupIssueListResponse,
  QuartrLookupIssueResponse,
  QuartrLookupIssueStatus,
  ReportFeedIssueRequest,
  ReportQuartrIssueRequest,
  SozoChannelsResponse,
  SozoServersResponse,
  UrlDiscoveryRequest,
  UrlDiscoveryResult,
} from '../types/contentCompletion';

export const getSozoServers = async (): Promise<SozoServersResponse> => {
  const response = await client.get('/api/v1/content-completion/servers');
  return response.data;
};

export const getSozoChannels = async (serverId: string): Promise<SozoChannelsResponse> => {
  const response = await client.get(`/api/v1/content-completion/servers/${serverId}/channels`);
  return response.data;
};

export const getChannelCompletionSummary = async (
  serverId: string,
  staleDays = 30
): Promise<ChannelCompletionSummaryResponse> => {
  const response = await client.get(
    `/api/v1/content-completion/servers/${serverId}/channels/summary`,
    { params: { stale_days: staleDays } }
  );
  return response.data;
};

export const getContentCompletionOverview = async (params?: {
  stale_days?: number;
  status?: ContentHealthStatus;
  quartr_status?: QuartrHealthStatus;
  corporate_status?: CorporateStatusFilter;
  coverage_priority?: CoveragePriorityFilter;
  search?: string;
  server_id?: string;
  channel_id?: string;
  sort_by?: ContentCompletionSortField;
  sort_order?: 'asc' | 'desc';
}, options?: { signal?: AbortSignal }): Promise<ContentCompletionOverviewResponse> => {
  const response = await client.get('/api/v1/content-completion/overview', {
    params,
    signal: options?.signal,
  });
  return response.data;
};

export const getCompanyContentDetail = async (
  companyId: string,
  staleDays = 30
): Promise<CompanyContentDetail> => {
  const response = await client.get(`/api/v1/content-completion/companies/${companyId}`, {
    params: { stale_days: staleDays },
  });
  return response.data;
};

export const startBackfillPreview = async (
  body: BackfillPreviewRequest
): Promise<{ run_id: string; status: string; message: string }> => {
  const response = await client.post('/api/v1/content-completion/backfill/preview', body);
  return response.data;
};

export const getBackfillPreviewStatus = async (runId: string): Promise<BackfillPreviewResult> => {
  const response = await client.get(`/api/v1/content-completion/backfill/preview/${runId}`);
  return response.data;
};

export const reportFeedIssue = async (body: ReportFeedIssueRequest): Promise<FeedUrlIssueResponse> => {
  const response = await client.post('/api/v1/content-completion/report-issue', body);
  return response.data;
};

export const getFeedUrlIssues = async (
  status: FeedUrlIssueStatus = 'open'
): Promise<FeedUrlIssueListResponse> => {
  const response = await client.get('/api/v1/content-completion/feed-issues', {
    params: { status },
  });
  return response.data;
};

export const resolveFeedUrlIssue = async (issueId: string): Promise<FeedUrlIssueResponse> => {
  const response = await client.post(`/api/v1/content-completion/feed-issues/${issueId}/resolve`);
  return response.data;
};

export const resolveFeedIssuesForCompany = async (
  companyId: string
): Promise<{ company_id: string; resolved_count: number }> => {
  const response = await client.post(
    `/api/v1/content-completion/feed-issues/resolve-company/${companyId}`
  );
  return response.data;
};

export const getQuartrLookupIssues = async (
  status: QuartrLookupIssueStatus = 'open'
): Promise<QuartrLookupIssueListResponse> => {
  const response = await client.get('/api/v1/content-completion/quartr-issues', {
    params: { status },
  });
  return response.data;
};

export const reportQuartrIssue = async (
  body: ReportQuartrIssueRequest
): Promise<QuartrLookupIssueResponse> => {
  const response = await client.post('/api/v1/content-completion/report-quartr-issue', body);
  return response.data;
};

export const resolveQuartrLookupIssue = async (
  issueId: string
): Promise<QuartrLookupIssueResponse> => {
  const response = await client.post(`/api/v1/content-completion/quartr-issues/${issueId}/resolve`);
  return response.data;
};

export const resolveQuartrIssuesForCompany = async (
  companyId: string
): Promise<{ company_id: string; resolved_count: number }> => {
  const response = await client.post(
    `/api/v1/content-completion/quartr-issues/resolve-company/${companyId}`
  );
  return response.data;
};

export const pollBackfillPreview = (
  runId: string,
  onUpdate: (result: BackfillPreviewResult) => void,
  intervalMs = 2000
): (() => void) => {
  let cancelled = false;

  const poll = async () => {
    while (!cancelled) {
      const result = await getBackfillPreviewStatus(runId);
      onUpdate(result);
      if (result.status !== 'running') {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  };

  poll().catch(() => {
    /* caller handles via onUpdate error state */
  });

  return () => {
    cancelled = true;
  };
};

export const startBackfillIngest = async (
  body: BackfillIngestRequest
): Promise<{ run_id: string; status: string; message: string }> => {
  const response = await client.post('/api/v1/content-completion/backfill/ingest', body);
  return response.data;
};

export const getBackfillIngestStatus = async (runId: string): Promise<BackfillIngestResult> => {
  const response = await client.get(`/api/v1/content-completion/backfill/ingest/${runId}`);
  return response.data;
};

export const getBackfillActivity = async (
  days = 7,
  limit = 100
): Promise<BackfillActivityListResponse> => {
  const response = await client.get('/api/v1/content-completion/backfill/activity', {
    params: { days, limit },
  });
  return response.data;
};

export const pollBackfillIngest = (
  runId: string,
  onUpdate: (result: BackfillIngestResult) => void,
  intervalMs = 2000
): (() => void) => {
  let cancelled = false;

  const poll = async () => {
    while (!cancelled) {
      const result = await getBackfillIngestStatus(runId);
      onUpdate(result);
      if (result.status !== 'running') {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  };

  poll().catch(() => {
    /* caller handles via onUpdate error state */
  });

  return () => {
    cancelled = true;
  };
};

export const startQuartrCheck = async (
  body: QuartrCheckRequest
): Promise<{ run_id: string; status: string; message: string }> => {
  const response = await client.post('/api/v1/content-completion/quartr/check', body);
  return response.data;
};

export const getQuartrCheckStatus = async (runId: string): Promise<QuartrCheckResult> => {
  const response = await client.get(`/api/v1/content-completion/quartr/check/${runId}`);
  return response.data;
};

export const pollQuartrCheck = (
  runId: string,
  onUpdate: (result: QuartrCheckResult) => void,
  intervalMs = 1500
): (() => void) => {
  let cancelled = false;

  const poll = async () => {
    while (!cancelled) {
      const result = await getQuartrCheckStatus(runId);
      onUpdate(result);
      if (result.status !== 'running') {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  };

  poll().catch(() => {
    /* caller handles via onUpdate error state */
  });

  return () => {
    cancelled = true;
  };
};

export const startUrlDiscoveryCheck = async (
  body: UrlDiscoveryRequest
): Promise<{ run_id: string; status: string; message: string }> => {
  const response = await client.post('/api/v1/content-completion/url-discovery/check', body);
  return response.data;
};

export const getUrlDiscoveryCheckStatus = async (runId: string): Promise<UrlDiscoveryResult> => {
  const response = await client.get(`/api/v1/content-completion/url-discovery/check/${runId}`);
  return response.data;
};

export const pollUrlDiscoveryCheck = (
  runId: string,
  onUpdate: (result: UrlDiscoveryResult) => void,
  intervalMs = 1500
): (() => void) => {
  let cancelled = false;

  const poll = async () => {
    while (!cancelled) {
      const result = await getUrlDiscoveryCheckStatus(runId);
      onUpdate(result);
      if (result.status !== 'running') {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  };

  poll().catch(() => {
    /* caller handles via onUpdate error state */
  });

  return () => {
    cancelled = true;
  };
};
